'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
var client = require('@apollo/client');
var growthbookReact = require('@growthbook/growthbook-react');
var nthHookLoma = require('@narvar/nth-hook-loma');
var nthHookContextAnalytics = require('@narvar/nth-hook-context-analytics');
var useLomaClient = require('./useLomaClient.js');
var ChatContent = require('./components/ChatContent.js');
var useChatUIState = require('./useChatUIState.js');
var nthHookContextPage = require('@narvar/nth-hook-context-page');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var React__default = /*#__PURE__*/_interopDefaultLegacy(React);

function tryGetSessionStorageItem(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch (err) {
    return null;
  }
}
const ChatImpl = () => {
  const [feedbackSubmitted, setFeedbackSubmitted] = React.useState(false);
  const {
    client: client$1
  } = useLomaClient["default"]();
  const logEvent = nthHookContextAnalytics.useAnalyticsPost();
  const sublimeDeliveryNotifications = growthbookReact.useFeatureIsOn('sublime-delivery-notifications');
  const {
    update: updateChatUIState
  } = useChatUIState["default"]();
  const {
    state
  } = nthHookContextPage.usePageContext();
  const {
    retailerMoniker,
    orderNumbers,
    trackingNumbers,
    trackingStatus,
    carrier
  } = state;
  const orderNumber = orderNumbers && orderNumbers[0] || '';
  const trackingNumber = trackingNumbers && trackingNumbers[0] || '';
  const {
    data: getUserPrefsNotificationPreferencesData,
    loading: getUserPrefsNotificationPreferencesLoading
  } = nthHookLoma.useGetUserPrefsNotificationPreferencesQuery({
    skip: !orderNumber || ['JUSTSHIPPED', 'INTRANSIT'].includes(trackingStatus ?? '') === false || sublimeDeliveryNotifications === false,
    variables: {
      orderNumber,
      retailerMoniker
    }
  });
  const sublimeSubscribed = getUserPrefsNotificationPreferencesData?.getUserPrefsNotificationPreferences?.optIns?.[0]?.preferences[0]?.status === 'active';

  // feedback ui state is kept in localstorage for now
  const localStorage = (() => {
    if (typeof window === 'undefined') return undefined;
    try {
      // reading the localStorage property can throw if we're in an iframe
      // see: https://stackoverflow.com/questions/67645164
      return window.localStorage;
    } catch (err) {
      return undefined;
    }
  })();

  // check if feedback has been submitted already before showing the cta
  // on load. see nth-pack-feedback for where this gets set on the track page.
  React.useEffect(() => {
    if (localStorage && trackingNumber && trackingStatus === 'DELIVERED') {
      try {
        const feedbackStorage = localStorage.getItem(`feedback_${trackingNumber}`);
        if (feedbackStorage) setFeedbackSubmitted(true);
      } catch (err) {
        // we can treat an error as no feedback submitted
      }
    }
  }, [trackingNumber, localStorage, trackingStatus]);

  // TODO: maybe abstract this logic out since we need chatConvo.length
  // in two places now.
  const clientSessionId = [retailerMoniker, carrier, trackingNumber, orderNumber].join('_');
  const storedState = React.useMemo(() => {
    const str = tryGetSessionStorageItem(`chat_${clientSessionId}`);
    if (str === null) return;
    const result = ChatContent.storedStateSchema.safeParse(JSON.parse(str));
    if (result.success) {
      return result.data;
    }
  }, [clientSessionId]);
  const chatConvo = storedState?.convo || [];
  const openSublimePrompt = !sublimeSubscribed && sublimeDeliveryNotifications && Boolean(orderNumber) && ['JUSTSHIPPED', 'INTRANSIT'].includes(trackingStatus ?? '');
  const openFeedbackPrompt = !feedbackSubmitted && trackingStatus === 'DELIVERED';
  const openChatOnLoad = (openSublimePrompt || openFeedbackPrompt) && chatConvo.length <= 1;
  React.useEffect(() => {
    const baseEventLogInfo = {
      eventComponent: nthHookContextAnalytics.NarvarEventComponents.NTH_KIT_CHAT,
      eventFeature: nthHookContextAnalytics.NarvarEventComponents.NTH_KIT_CHAT,
      eventType: nthHookContextAnalytics.NarvarEventTypes.COMPONENT_RENDER,
      pageComponent: nthHookContextAnalytics.NarvarEventComponents.NTH_KIT_CHAT,
      chatClientSessionId: clientSessionId
    };
    if (openFeedbackPrompt === true) {
      logEvent({
        ...baseEventLogInfo,
        feature: 'feedback'
      });
    } else if (openSublimePrompt === true) {
      logEvent({
        ...baseEventLogInfo,
        feature: 'delivery-notifications'
      });
    } else {
      logEvent({
        ...baseEventLogInfo,
        feature: 'default'
      });
    }
  }, [logEvent, openFeedbackPrompt, openSublimePrompt, clientSessionId]);
  React.useEffect(() => {
    // TODO: temporarily moved the <ChatProvider/> to track page level so the
    // help pill can access state. this is initial state that gets passed to
    // that provider here in the chat component. probably want to move it back.
    updateChatUIState({
      hadChatInteraction: false,
      openOnLoad: openChatOnLoad,
      chatOpen: openChatOnLoad,
      chatExists: true
    });
  }, [openChatOnLoad, updateChatUIState]);
  if (!client$1 || getUserPrefsNotificationPreferencesLoading) return null;
  return /*#__PURE__*/React__default["default"].createElement(client.ApolloProvider, {
    client: client$1
  }, /*#__PURE__*/React__default["default"].createElement(ChatContent["default"], {
    retailerMoniker: retailerMoniker,
    orderNumber: orderNumber,
    trackingNumber: trackingNumber,
    carrierMoniker: carrier || ''
  }));
};
const IfChatEnabled = props => {
  const {
    client: client$1
  } = useLomaClient["default"]();
  const growthbook = growthbookReact.useGrowthBook();
  const [status, setStatus] = React.useState('pending');
  const attributes = growthbook?.getAttributes();
  const deviceLayout = attributes?.['track_deviceLayout'];
  const retailerMoniker = attributes?.['track_retailerMoniker'];
  React.useEffect(() => {
    if (!client$1) {
      return;
    }
    const query = client.gql`
      query ChatEnabled($deviceLayout: String!, $retailerMoniker: String!) {
        chatEnabled(
          attributes: {
            deviceLayout: $deviceLayout
            retailerMoniker: $retailerMoniker
          }
        )
      }
    `;
    (async () => {
      const response = await client$1.query({
        query,
        variables: {
          retailerMoniker,
          deviceLayout
        }
      });
      if (response.data.chatEnabled) {
        setStatus('enabled');
      } else {
        setStatus('disabled');
      }
    })().catch(err => {
      // eslint-disable-next-line no-console
      console.error(err);
      setStatus('rejected');
    });
  }, [client$1, deviceLayout, retailerMoniker]);
  if (status === 'enabled') {
    return /*#__PURE__*/React__default["default"].createElement(React__default["default"].Fragment, null, props.children);
  }
  return null;
};
const IfSessionLoaded = props => {
  const [status, setStatus] = React.useState('pending');
  React.useEffect(() => {
    (async () => {
      const response = await fetch('/loma/api/sessions', {
        method: 'POST'
      });
      if (!response.ok) {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
      setStatus('resolved');
    })().catch(err => {
      // eslint-disable-next-line no-console
      console.error(err);
      setStatus('rejected');
    });
  }, []);
  if (status === 'pending') {
    return null;
  }
  if (status === 'rejected') {
    // TODO: render an error state
    return null;
  }
  return /*#__PURE__*/React__default["default"].createElement(React__default["default"].Fragment, null, props.children);
};
const Chat = () => /*#__PURE__*/React__default["default"].createElement(IfChatEnabled, null, /*#__PURE__*/React__default["default"].createElement(IfSessionLoaded, null, /*#__PURE__*/React__default["default"].createElement(ChatImpl, null)));

exports.ChatProvider = useChatUIState.ChatProvider;
exports.useChatUIState = useChatUIState["default"];
exports["default"] = Chat;
